import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZxcvbnFunction, ZxcvbnResult } from '../../types/zxcvbn.types';

// Import zxcvbn - using require for compatibility
interface ZxcvbnModule {
  default?: ZxcvbnFunction;
  (password: string): ZxcvbnResult;
}
type ZxcvbnRequireResult = ZxcvbnModule | ZxcvbnFunction;
declare const require: (module: string) => unknown;

let zxcvbn: ZxcvbnFunction;
try {
  const zxcvbnModule = require('zxcvbn') as ZxcvbnRequireResult;
  if (typeof zxcvbnModule === 'function') {
    zxcvbn = zxcvbnModule;
  } else {
    const moduleWithDefault = zxcvbnModule as ZxcvbnModule;
    zxcvbn = (moduleWithDefault.default || moduleWithDefault) as ZxcvbnFunction;
  }
} catch {
  // Fallback if zxcvbn is not available
  const fallbackResult: ZxcvbnResult = { score: 0, feedback: { suggestions: [], warning: '' } };
  zxcvbn = () => fallbackResult;
}

@Component({
  selector: 'app-password-strength-meter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './password-strength-meter.component.html',
  styleUrls: ['./password-strength-meter.component.scss'],
})
export class PasswordStrengthMeterComponent implements OnChanges {
  @Input() password: string = '';
  @Input() enableFeedback: boolean = true;

  strength: number = 0;
  feedback: string = '';
  scoreLabel: string = '';
  scoreColor: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['password']) {
      this.calculateStrength();
    }
  }

  calculateStrength(): void {
    if (!this.password) {
      this.strength = 0;
      this.feedback = '';
      this.scoreLabel = '';
      this.scoreColor = '';
      return;
    }

    const result = zxcvbn(this.password);
    this.strength = result.score; // 0-4

    // Set label and color based on score
    switch (this.strength) {
      case 0:
        this.scoreLabel = 'Very Weak';
        this.scoreColor = '#dc3545';
        break;
      case 1:
        this.scoreLabel = 'Weak';
        this.scoreColor = '#fd7e14';
        break;
      case 2:
        this.scoreLabel = 'Fair';
        this.scoreColor = '#ffc107';
        break;
      case 3:
        this.scoreLabel = 'Good';
        this.scoreColor = '#28a745';
        break;
      case 4:
        this.scoreLabel = 'Strong';
        this.scoreColor = '#20c997';
        break;
      default:
        this.scoreLabel = '';
        this.scoreColor = '';
    }

    // Get feedback
    if (this.enableFeedback && result.feedback.suggestions.length > 0) {
      this.feedback = result.feedback.suggestions[0];
    } else if (this.enableFeedback && result.feedback.warning) {
      this.feedback = result.feedback.warning;
    } else {
      this.feedback = '';
    }
  }

  getStrengthPercentage(): number {
    return (this.strength / 4) * 100;
  }
}
